/**
 * Reusable AJAX Add to Cart
 *
 * Adds a variant to the Shopify cart via AJAX, refreshes the cart drawer,
 * and opens it. Works with any button that has [data-ajax-atc].
 *
 * Usage (HTML):
 *   <button class="my-btn" data-ajax-atc data-variant-id="12345" data-quantity="1">
 *     Add to Cart
 *   </button>
 *
 * Usage (JS – call directly from anywhere):
 *   window.SB.addToCart(variantId, quantity, triggerButton);
 */

(function () {
  'use strict';

  window.SB = window.SB || {};

  /**
   * Add a variant to the Shopify cart, refresh + open the cart drawer.
   *
   * @param {string|number} variantId  – Shopify variant ID
   * @param {number}        [quantity=1] – quantity to add
   * @param {HTMLElement}   [triggerBtn] – optional button to show loading state
   * @returns {Promise<object>} – the parsed JSON response from /cart/add.js
   */
  window.SB.addToCart = async function (variantId, quantity, triggerBtn) {
    if (!variantId) {
      console.error('[SB.addToCart] No variantId provided');
      return;
    }

    quantity = parseInt(quantity, 10) || 1;

    // --- Loading state -----------------------------------------------------------
    let originalHTML = '';
    if (triggerBtn) {
      originalHTML = triggerBtn.innerHTML;
      triggerBtn.setAttribute('aria-disabled', 'true');
      triggerBtn.classList.add('ajax-atc--loading');
    }

    // --- Build sections list so Shopify returns rendered cart-drawer HTML ---------
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRequest = [];
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach(function (s) {
        sectionsToRequest.push(s.id);
      });
    }

    // --- POST to /cart/add.js ----------------------------------------------------
    const body = {
      items: [{ id: parseInt(variantId, 10), quantity: quantity }],
    };
    if (sectionsToRequest.length) {
      body.sections = sectionsToRequest.join(',');
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(
        window.routes ? window.routes.cart_add_url : '/cart/add.js',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript',
          },
          body: JSON.stringify(body),
        }
      );

      const data = await response.json();

      if (data.status && data.status !== 200) {
        // Shopify returned an error (e.g. out of stock)
        console.error('[SB.addToCart] Error:', data.description || data.message);
        if (triggerBtn) {
          triggerBtn.innerHTML = '<span>Error – try again</span>';
          setTimeout(function () {
            triggerBtn.innerHTML = originalHTML;
            triggerBtn.removeAttribute('aria-disabled');
            triggerBtn.classList.remove('ajax-atc--loading');
          }, 2000);
        }
        return data;
      }

      // --- Refresh & open the cart drawer ----------------------------------------
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        // Fallback: just open the drawer (older themes without renderContents)
        cartDrawer.open();
      }

      // Dispatch a custom event so other scripts (cart bubble, analytics) can react
      document.dispatchEvent(
        new CustomEvent('product:added-to-cart', {
          detail: { variantId: variantId, quantity: quantity, cart: data },
        })
      );

      return data;
    } catch (err) {
      console.error('[SB.addToCart] Fetch failed:', err);
      if (triggerBtn) {
        triggerBtn.innerHTML = '<span>Error – try again</span>';
        setTimeout(function () {
          triggerBtn.innerHTML = originalHTML;
        }, 2000);
      }
    } finally {
      if (triggerBtn) {
        triggerBtn.removeAttribute('aria-disabled');
        triggerBtn.classList.remove('ajax-atc--loading');
      }
    }
  };

  // --- Auto-bind buttons with [data-ajax-atc] -----------------------------------
  function bindButtons() {
    document.querySelectorAll('[data-ajax-atc]').forEach(function (btn) {
      if (btn.dataset.ajaxAtcBound) return; // prevent double-binding
      btn.dataset.ajaxAtcBound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var vid = btn.getAttribute('data-variant-id') || btn.closest('[data-variant-id]')?.getAttribute('data-variant-id');
        var qty = btn.getAttribute('data-quantity') || btn.closest('[data-quantity]')?.getAttribute('data-quantity') || 1;
        window.SB.addToCart(vid, qty, btn);
      });
    });
  }

  // Bind on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindButtons);
  } else {
    bindButtons();
  }

  // Re-bind after Shopify section rendering (useful for section-rendering + AJAX pages)
  document.addEventListener('shopify:section:load', bindButtons);
})();
